import React from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Helper function to handle navigation to sections
  const handleSectionNavigation = (sectionId: string) => {
    if (location.pathname === '/') {
      // On home page, smooth scroll to section
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else {
      // On other page, navigate to home with hash
      navigate(`/#${sectionId}`);
    }
  };

  const isBlogPage = location.pathname.startsWith('/blog');

  return (
    <footer className="relative bg-accent text-background overflow-hidden" role="contentinfo">

      {/* Local grid overlay — only on blog pages where global hero-grid is hidden */}
      {isBlogPage && (
        <div className="hero-grid" aria-hidden="true" style={{ position: 'absolute', zIndex: 0 }} />
      )}
      
      <div className="section-grain" aria-hidden="true" style={{ '--section-grain-light-opacity': '0.02' } as React.CSSProperties} />
      <div className="relative container mx-auto px-6 py-16">
        {/* Clean centered layout */}
        <div className="text-center max-w-2xl mx-auto">
          {/* Brand */}
          <button 
            onClick={(e) => {
              e.preventDefault();
              if (location.pathname === '/') {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              } else {
                navigate('/');
              }
            }}
            className="inline-block mb-6 focus-ring rounded-sm"
            aria-label="Paokhum homepage"
          >
            <img 
              src="/Paokhum_logo_white.svg" 
              alt="Paokhum" 
              width="403"
              height="40"
              className="h-10 w-auto mx-auto"
            />
          </button>
          
          {/* Description */}
          <p className="text-background/80 text-base mb-8 leading-relaxed">
            Building modern web experiences and seamless CCTV integrations for businesses across India.
          </p>
          
          {/* Simple navigation links in clean rows */}
          <div className="space-y-6 mb-8">
            {/* Services Row */}
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
              <button 
                onClick={() => handleSectionNavigation('services')}
                className="text-background/70 hover:text-primary transition-colors text-sm font-medium hover:underline px-2 py-1 bg-transparent border-none cursor-pointer"
              >
                Services
              </button>
              <button 
                onClick={() => handleSectionNavigation('portfolio')}
                className="text-background/70 hover:text-primary transition-colors text-sm font-medium hover:underline px-2 py-1 bg-transparent border-none cursor-pointer"
              >
                Portfolio
              </button>
              <button 
                onClick={() => handleSectionNavigation('pricing')}
                className="text-background/70 hover:text-primary transition-colors text-sm font-medium hover:underline px-2 py-1 bg-transparent border-none cursor-pointer"
              >
                Pricing
              </button>
              <button 
                onClick={() => handleSectionNavigation('about')}
                className="text-background/70 hover:text-primary transition-colors text-sm font-medium hover:underline px-2 py-1 bg-transparent border-none cursor-pointer"
              >
                About
              </button>
            </div>
          </div>
        </div>

        {/* Clean bottom section */}
        <div className="border-t border-background/20 pt-8">
          <div className="text-center space-y-4">
            {/* Legal links */}
            <div className="flex justify-center gap-6">
              <Link
                to="/privacy"
                className="text-sm text-background/60 hover:text-primary transition-colors hover:underline"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-sm text-background/60 hover:text-primary transition-colors hover:underline"
              >
                Terms of Service
              </Link>
            </div>
            
            {/* Copyright */}
            <p className="text-sm text-background/60">
              © {currentYear} Paokhum. All rights reserved.
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
